/**
 * AlertManager Component
 * 
 * A comprehensive interface for managing existing alerts in the DeepRDashboard alert system.
 * Features:
 * - Alert listing with filtering and sorting
 * - Edit and delete functionality
 * - Enable/disable toggle for alerts
 * - Server filter for viewing alerts by server
 * - Pagination for large alert sets
 * - Responsive design with theme support
 * 
 * @module AlertManager
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAlertContext } from '../../contexts/AlertContext';
import DirectModal from './DirectModal'; // Use our direct modal implementation
import AlertFormInitializer from '../AlertForm/AlertFormInitializer';
import AuthService from '../../services/AuthService';
// Import using ES module syntax
import { 
  getAlertTypeDisplayText, 
  getSeverityDisplayText,
  getAlertTypeIcon,
  getSeverityIcon
} from '../../models/AlertModels';
import { EditIcon, DeleteIcon, SortUpIcon, SortDownIcon } from '../../components/Icons';
import QWebToggle from '../common/QWebToggle';
import '../common/QWebToggleStyleLoader';

/**
 * AlertManager component for managing existing alerts
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.hideTitle - Whether to hide the title (useful when displayed in a Modal)
 * @returns {JSX.Element} AlertManager component
 */
function AlertManager({ hideTitle = false }) {
  const { 
    alerts, 
    deleteAlert, 
    toggleAlertEnabled, 
    startEditAlert,
    refreshAlerts,
    isLoading,
    error 
  } = useAlertContext();
  
  // State for filtering and sorting
  const [filteredAlerts, setFilteredAlerts] = useState([]);
  const [filters, setFilters] = useState({
    type: '',
    severity: '',
    serverId: '',
    searchText: ''
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'updatedAt',
    direction: 'desc'
  });
  
  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  
  // State for edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  
  // State for confirmation dialog
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState('');
  
  // Server list (would typically come from a context or service)
  const [servers, setServers] = useState([]);
  
  // Load servers (placeholder - would be replaced with actual API call)
  useEffect(() => {
    // This is a placeholder - in a real implementation, we would fetch servers from an API
    setServers([
      { id: 'server1', name: 'Production Server' },
      { id: 'server2', name: 'Development Server' },
      { id: 'server3', name: 'Testing Server' }
    ]);
  }, []);
  
  /**
   * Apply filters and sorting to alerts
   */
  const applyFiltersAndSort = useCallback(() => {
    if (!alerts || alerts.length === 0) {
      setFilteredAlerts([]);
      setTotalPages(1);
      return;
    }
    
    // Apply filters
    let result = [...alerts];
    
    if (filters.type) {
      result = result.filter(alert => alert.type === filters.type);
    }
    
    if (filters.severity) {
      result = result.filter(alert => alert.severity === filters.severity);
    }
    
    if (filters.serverId) {
      result = result.filter(alert => 
        alert.scope.type === 'server-specific' && 
        alert.scope.serverIds && 
        alert.scope.serverIds.includes(filters.serverId)
      );
    }
    
    if (filters.searchText) {
      const searchLower = filters.searchText.toLowerCase();
      result = result.filter(alert => 
        alert.name.toLowerCase().includes(searchLower) || 
        (alert.description && alert.description.toLowerCase().includes(searchLower))
      );
    }
    
    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    // Update total pages
    setTotalPages(Math.max(1, Math.ceil(result.length / itemsPerPage)));
    
    // Apply pagination
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedResult = result.slice(startIndex, startIndex + itemsPerPage);
    
    setFilteredAlerts(paginatedResult);
  }, [alerts, filters, sortConfig, currentPage, itemsPerPage]);
  
  // Apply filters and sorting whenever dependencies change
  useEffect(() => {
    applyFiltersAndSort();
  }, [applyFiltersAndSort]);
  
  /**
   * Handle filter change
   * @param {Object} newFilters - New filter values
   */
  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setCurrentPage(1); // Reset to first page when filters change
  };
  
  /**
   * Handle sort change
   * @param {string} key - Key to sort by
   */
  const handleSortChange = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key ? (prev.direction === 'asc' ? 'desc' : 'asc') : 'asc'
    }));
  };
  
  /**
   * Handle page change
   * @param {number} page - Page number
   */
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  
  /**
   * Handle edit alert
   * @param {string} alertId - ID of the alert to edit
   */
  const handleEditAlert = (alertId) => {
    startEditAlert(alertId);
    setShowEditModal(true);
  };
  
  /**
   * Handle delete alert confirmation
   * @param {string} alertId - ID of the alert to delete
   * @param {string} alertName - Name of the alert to delete
   */
  const handleDeleteConfirmation = (alertId, alertName) => {
    setConfirmMessage(`Are you sure you want to delete the alert "${alertName}"? This action cannot be undone.`);
    setConfirmAction(() => async () => {
      try {
        await deleteAlert(alertId);
        setShowConfirmDialog(false);
      } catch (error) {
        console.error('Error deleting alert:', error);
      }
    });
    setShowConfirmDialog(true);
  };
  
  /**
   * Handle toggle alert enabled state
   * @param {string} alertId - ID of the alert to toggle
   * @param {boolean} currentState - Current enabled state
   */
  const handleToggleEnabled = (alertId, currentState) => {
    // Show loading state for this specific alert
    const updatingAlertId = alertId;
    setFilteredAlerts(prev => prev.map(alert => 
      alert.id === alertId ? {...alert, isUpdating: true} : alert
    ));
    
    toggleAlertEnabled(alertId)
      .then(updatedAlert => {
        // Success! The updated alert will be reflected in state during the next refresh
        // No need to do anything special here as the context will update the alerts array
      })
      .catch(error => {
        console.error('Error toggling alert state:', error);
        
        // Revert the toggle visual state to the original state
        setFilteredAlerts(prev => prev.map(alert => 
          alert.id === alertId ? {...alert, isUpdating: false, enabled: currentState} : alert
        ));
        
        // Show user-friendly error message
        const errorMessage = error?.message || 'Failed to toggle alert status';
        alert(`Error: ${errorMessage}`);
      });
  };
  
  /**
   * Render sort indicator
   * @param {string} key - Key being sorted
   * @returns {JSX.Element|null} Sort indicator
   */
  const renderSortIndicator = (key) => {
    if (sortConfig.key !== key) {
      return null;
    }
    
    return (
      <span className={`sort-indicator ${sortConfig.direction}`}>
        {sortConfig.direction === 'asc' ? '↑' : '↓'}
      </span>
    );
  };
  
  /**
   * Render pagination controls
   * @returns {JSX.Element} Pagination controls
   */
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    
    const pages = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button 
          key={i}
          className={`pagination-button ${currentPage === i ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }
    
    return (
      <div className="alert-manager-pagination">
        <button 
          className="pagination-button"
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
        >
          &laquo;
        </button>
        <button 
          className="pagination-button"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          &lsaquo;
        </button>
        
        {startPage > 1 && (
          <>
            <button 
              className="pagination-button"
              onClick={() => handlePageChange(1)}
            >
              1
            </button>
            {startPage > 2 && <span className="pagination-ellipsis">...</span>}
          </>
        )}
        
        {pages}
        
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="pagination-ellipsis">...</span>}
            <button 
              className="pagination-button"
              onClick={() => handlePageChange(totalPages)}
            >
              {totalPages}
            </button>
          </>
        )}
        
        <button 
          className="pagination-button"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          &rsaquo;
        </button>
        <button 
          className="pagination-button"
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
        >
          &raquo;
        </button>
        
        <div className="items-per-page">
          <label htmlFor="items-per-page">Items per page:</label>
          <select 
            id="items-per-page"
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>
    );
  };
  
  /**
   * Format timestamp to readable date
   * @param {number} timestamp - Unix timestamp
   * @returns {string} Formatted date
   */
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  if (isLoading) {
    return <div className="alert-manager loading">Loading alerts...</div>;
  }
  
  if (error) {
    return (
      <div className="alert-manager error">
        <p>Error loading alerts: {error.message}</p>
        <button onClick={refreshAlerts}>Retry</button>
      </div>
    );
  }

  // No debug information needed
  
  return (
    <div className="alert-manager-container">
      {!hideTitle && (
        <h2 className="alert-manager-title">Manage Alerts</h2>
      )}

      {/* Filters */}
      <div className="alert-manager-filters">
        <div className="filter-group">
          <label htmlFor="type-filter">Type:</label>
          <select 
            id="type-filter"
            value={filters.type}
            onChange={(e) => handleFilterChange({ type: e.target.value })}
          >
            <option value="">All Types</option>
            <option value="Global">Global</option>
            <option value="SystemEvent">System Event</option>
            <option value="Process">Process</option>
            <option value="Log">Log</option>
          </select>
        </div>
        
        <div className="filter-group">
          <label htmlFor="severity-filter">Severity:</label>
          <select 
            id="severity-filter"
            value={filters.severity}
            onChange={(e) => handleFilterChange({ severity: e.target.value })}
          >
            <option value="">All Severities</option>
            <option value="Info">Info</option>
            <option value="Warning">Warning</option>
            <option value="Critical">Critical</option>
          </select>
        </div>
        
        <div className="filter-group">
          <label htmlFor="server-filter">Server:</label>
          <select 
            id="server-filter"
            value={filters.serverId}
            onChange={(e) => handleFilterChange({ serverId: e.target.value })}
          >
            <option value="">All Servers</option>
            {servers.map(server => (
              <option key={server.id} value={server.id}>
                {server.name}
              </option>
            ))}
          </select>
        </div>
        
        <div className="filter-group search-group">
          <label htmlFor="alert-search">Search</label>
          <input 
            id="alert-search"
            type="text"
            placeholder="Search alerts by name or description"
            value={filters.searchText}
            onChange={(e) => handleFilterChange({ searchText: e.target.value })}
            className="search-input"
          />
        </div>
      </div>
      
      {/* Alert Table */}
      <div className="alert-manager-table-container">
        {isLoading ? (
          <div className="loading-indicator">Loading alerts...</div>
        ) : error ? (
          <div className="error-message">Error loading alerts: {error}</div>
        ) : filteredAlerts.length === 0 ? (
          <div className="empty-state">
            <p>No alerts found matching your criteria.</p>
          </div>
        ) : (
          <table className="alert-manager-table">
            <thead>
              <tr>
                <th 
                  className="sortable"
                  onClick={() => handleSortChange('name')}
                >
                  Name {renderSortIndicator('name')}
                </th>
                <th 
                  className="sortable"
                  onClick={() => handleSortChange('type')}
                >
                  Type {renderSortIndicator('type')}
                </th>
                <th 
                  className="sortable"
                  onClick={() => handleSortChange('severity')}
                >
                  Severity {renderSortIndicator('severity')}
                </th>
                <th>Scope</th>
                <th 
                  className="sortable"
                  onClick={() => handleSortChange('updatedAt')}
                >
                  Last Updated {renderSortIndicator('updatedAt')}
                </th>
                <th 
                  className="sortable"
                  onClick={() => handleSortChange('enabled')}
                >
                  Status {renderSortIndicator('enabled')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAlerts.map(alert => (
                <tr key={alert.id} className={!alert.enabled ? 'disabled-alert' : ''}>
                  <td>{alert.name}</td>
                  <td>
                    <div className="alert-type">
                      <span className={`alert-type-icon ${alert.type.toLowerCase()}`}>
                        {getAlertTypeIcon(alert.type)}
                      </span>
                      <span>{getAlertTypeDisplayText(alert.type)}</span>
                    </div>
                  </td>
                  <td>
                    <div className={`alert-severity ${alert.severity.toLowerCase()}`}>
                      <span className="severity-icon">
                        {getSeverityIcon(alert.severity)}
                      </span>
                      <span>{getSeverityDisplayText(alert.severity)}</span>
                    </div>
                  </td>
                  <td>
                    {alert.scope.type === 'global' ? (
                      <span className="global-scope">Global</span>
                    ) : (
                      <span className="server-scope">
                        {alert.scope.serverIds?.length || 0} Server(s)
                      </span>
                    )}
                  </td>
                  <td>{formatDate(alert.updatedAt)}</td>
                  <td>
                    <QWebToggle
                      checked={alert.enabled}
                      onChange={() => handleToggleEnabled(alert.id, alert.enabled)}
                      className="qweb-compatible-toggle"
                    />
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="edit-button"
                        onClick={() => handleEditAlert(alert.id)}
                        aria-label={`Edit alert ${alert.name}`}
                        title="Edit alert"
                      >
                        <span className="edit-icon" aria-hidden="true">✏️</span>
                      </button>
                      <button 
                        className="delete-button"
                        onClick={() => handleDeleteConfirmation(alert.id, alert.name)}
                        aria-label={`Delete alert ${alert.name}`}
                        title="Delete alert"
                      >
                        <span className="delete-icon" aria-hidden="true">🗑️</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      
      {/* Pagination */}
      {renderPagination()}
      
      {/* Edit Modal - Using DirectModal for guaranteed visibility */}
      <DirectModal
        isOpen={showEditModal}
        onClose={() => {
          // When closing the edit modal, show a confirmation dialog if there are unsaved changes
          setConfirmMessage('Are you sure you want to cancel editing? Any unsaved changes will be lost.');
          
          // Create the action to run when user confirms they want to close the modal
          setConfirmAction(() => () => {
            // Clear any editing state in the context
            // This is important to prevent crashes when re-opening the form
            startEditAlert(null); // Reset the edit state by passing null
            setShowEditModal(false); // Close the edit modal
            setShowConfirmDialog(false); // Close the confirmation dialog
          });
          
          // Show the confirmation dialog
          setShowConfirmDialog(true);
        }}
        title="Edit Alert"
        size="large"
      >
        <AlertFormInitializer hideTitle={true} />
      </DirectModal>
      
      {/* Confirmation Dialog - Using DirectModal for guaranteed visibility */}
      <DirectModal
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        title="Confirm Action"
        size="small"
        footer={
          <div className="confirm-dialog-buttons">
            <button 
              className="btn btn-secondary"
              onClick={() => setShowConfirmDialog(false)}
              style={{
                padding: '8px 16px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                marginRight: '10px'
              }}
            >
              Cancel
            </button>
            <button 
              className="btn btn-primary"
              onClick={confirmAction}
              style={{
                padding: '8px 16px',
                backgroundColor: confirmMessage.includes('delete') ? '#dc3545' : '#4a90e2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {confirmMessage.includes('delete') ? 'Delete' : 'Close'}
            </button>
          </div>
        }
      >
        <p className="confirm-dialog-message" style={{ fontSize: '1rem', lineHeight: '1.5' }}>
          {confirmMessage}
        </p>
      </DirectModal>
    </div>
  );
}

export default AlertManager;
